import { purryFromLazy } from "./internal/purryFromLazy";
import type { IterableContainer } from "./internal/types/IterableContainer";
import type { LazyEvaluator } from "./internal/types/LazyEvaluator";
import type { Mapped } from "./internal/types/Mapped";

/**
 * Applies a function on each element of the array, using the result of the
 * previous application, and returns an array of the successively computed
 * values.
 *
 * @param data - The array to map over.
 * @param callbackfn - The callback function that receives the previous value,
 * the current element.
 * @param initialValue - The initial value to start the computation with.
 * @returns An array of successively computed values from the left side of the
 * array.
 * @signature
 *    mapWithFeedback(data, callbackfn, initialValue);
 * @example
 *    mapWithFeedback(
 *      [1, 2, 3, 4, 5],
 *      (prev, x) => prev + x,
 *      100,
 *    ); // => [101, 103, 106, 110, 115]
 * @dataFirst
 * @lazy
 * @category Array
 */
export function mapWithFeedback<T extends IterableContainer, U>(
  data: T,
  callbackfn: (
    previousValue: U,
    currentValue: T[number],
    currentIndex: number,
    data: T,
  ) => U,
  initialValue: U,
): Mapped<T, U>;

/**
 * Applies a function on each element of the array, using the result of the
 * previous application, and returns an array of the successively computed
 * values.
 *
 * @param callbackfn - The callback function that receives the previous value,
 * the current element.
 * @param initialValue - The initial value to start the computation with.
 * @returns An array of successively computed values from the left side of the
 * array.
 * @signature
 *    mapWithFeedback(callbackfn, initialValue)(data);
 * @example
 *    pipe(
 *      [1, 2, 3, 4, 5],
 *      mapWithFeedback((prev, x) => prev + x, 100),
 *    ); // => [101, 103, 106, 110, 115]
 * @dataLast
 * @lazy
 * @category Array
 */
export function mapWithFeedback<T extends IterableContainer, U>(
  callbackfn: (
    previousValue: U,
    currentValue: T[number],
    currentIndex: number,
    data: T,
  ) => U,
  initialValue: U,
): (data: T) => Mapped<T, U>;

export function mapWithFeedback(...args: readonly unknown[]): unknown {
  return purryFromLazy(lazyImplementation, args);
}

const lazyImplementation = <T, U>(
  reducer: (
    previousValue: U,
    currentValue: T,
    index: number,
    data: readonly T[],
  ) => U,
  initialValue: U,
): LazyEvaluator<T, U> => {
  let previousValue = initialValue;
  return (currentValue, index, data) => {
    previousValue = reducer(previousValue, currentValue, index, data);
    return { done: false, hasNext: true, next: previousValue };
  };
};
